import { useRef, useState, type ReactNode } from 'react';
import { Preloader } from 'konsta/react';
import { Camera, Check, MapPin, Phone, Plus, X } from 'lucide-react';
import type { City } from '../api';
import { api } from '../api';
import { haptic, requestLocation, requestPhone } from '../telegram';
import { useErrorText, useName, useT } from '../i18n';

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string | null; children: ReactNode }) {
  return (
    // A div, not a <label>: a label would forward taps to the first button inside it.
    <div className="mb-5" role="group" aria-label={label}>
      <span className="mb-1.5 block text-sm font-semibold">{label}</span>
      {children}
      {error ? <span className="mt-1 block text-sm text-red-500">{error}</span> : hint ? <span className="mt-1 block text-sm text-tg-hint">{hint}</span> : null}
    </div>
  );
}

export const inputClass =
  'w-full rounded-xl bg-tg-secondary px-4 py-3.5 text-base text-tg-text outline-none placeholder:text-tg-hint focus:ring-2 focus:ring-tg-button';

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={inputClass + ' ' + (props.className ?? '')} />;
}

/** Two big pill buttons: Male / Female. */
export function GenderPicker({ value, onChange }: { value?: 'MALE' | 'FEMALE'; onChange: (g: 'MALE' | 'FEMALE') => void }) {
  const t = useT();
  return (
    <div className="grid grid-cols-2 gap-3">
      {(['FEMALE', 'MALE'] as const).map((g) => (
        <button
          type="button"
          key={g}
          onClick={() => {
            haptic('select');
            onChange(g);
          }}
          className={`rounded-xl py-3.5 font-semibold ${value === g ? 'bg-tg-button text-tg-buttonText' : 'bg-tg-secondary'}`}
        >
          {t(`gender.${g}`)}
        </button>
      ))}
    </div>
  );
}

/** Share the Telegram number with one tap, or type it. */
export function PhoneField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const t = useT();
  const [asking, setAsking] = useState(false);
  return (
    <div className="space-y-2">
      <button
        type="button"
        disabled={asking}
        onClick={async () => {
          setAsking(true);
          const phone = await requestPhone();
          setAsking(false);
          if (phone) {
            haptic('success');
            onChange(phone.startsWith('+') ? phone : '+' + phone);
          }
        }}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-tg-secondary py-3 font-semibold text-tg-link"
      >
        {asking ? <Preloader className="h-5 w-5" /> : <Phone size={18} />}
        {t('reg.share_phone')}
      </button>
      <TextInput type="tel" inputMode="tel" autoComplete="tel" placeholder={t('reg.phone_placeholder')} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export interface LocationValue {
  lat?: number;
  lng?: number;
  cityId?: string;
}

/** Current GPS location (preferred) or a city from the list. */
export function LocationField({ value, onChange, cities }: { value: LocationValue; onChange: (v: LocationValue) => void; cities: City[] }) {
  const t = useT();
  const name = useName();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const hasGps = value.lat !== undefined && value.lng !== undefined;

  return (
    <div className="space-y-3">
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setFailed(false);
          const loc = await requestLocation();
          setBusy(false);
          if (loc) {
            haptic('success');
            onChange({ lat: loc.lat, lng: loc.lng, cityId: undefined });
          } else setFailed(true);
        }}
        className={`flex w-full items-center justify-center gap-2 rounded-xl py-3.5 font-semibold ${
          hasGps ? 'bg-green-500/15 text-green-700' : 'bg-tg-button text-tg-buttonText'
        }`}
      >
        {busy ? <Preloader className="h-5 w-5" /> : hasGps ? <Check size={18} /> : <MapPin size={18} />}
        {hasGps ? t('reg.location_saved') : t('reg.use_my_location')}
      </button>
      {failed && <p className="text-sm text-red-500">{t('reg.location_failed')}</p>}
      <div>
        <span className="mb-1.5 block text-sm text-tg-hint">{t('reg.or_choose_city')}</span>
        <select
          className={inputClass}
          value={hasGps ? '' : value.cityId ?? ''}
          onChange={(e) => onChange({ cityId: e.target.value || undefined })}
        >
          <option value="">{t('reg.city')}</option>
          {cities.map((c) => (
            <option key={c.id} value={c.id}>
              {name(c)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

type Purpose = 'provider_photo' | 'id_photo' | 'id_selfie';

function useUpload(purpose: Purpose) {
  const errText = useErrorText();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      return await api.upload(purpose, file);
    } catch (e) {
      haptic('error');
      setError(errText(e));
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

/** Instagram-style grid of up to `max` square photos. */
export function PhotoGrid({
  photos,
  onChange,
  max = 3,
}: {
  photos: Array<{ key: string; url: string }>;
  onChange: (p: Array<{ key: string; url: string }>) => void;
  max?: number;
}) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const up = useUpload('provider_photo');
  return (
    <div>
      <div className="grid grid-cols-3 gap-2">
        {photos.map((p, i) => (
          <div key={p.key} className="relative aspect-square overflow-hidden rounded-xl bg-tg-secondary">
            <img src={p.url} className="h-full w-full object-cover" alt="" />
            <button
              type="button"
              onClick={() => onChange(photos.filter((_, j) => j !== i))}
              className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"
              aria-label="Remove"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        {photos.length < max && (
          <button
            type="button"
            disabled={up.busy}
            onClick={() => input.current?.click()}
            className="flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-tg-hint text-tg-hint"
          >
            {up.busy ? <Preloader /> : <Plus size={28} />}
            <span className="text-xs">{up.busy ? t('reg.uploading') : t('reg.add_photo')}</span>
          </button>
        )}
      </div>
      {up.error && <p className="mt-2 text-sm text-red-500">{up.error}</p>}
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          const r = await up.run(f);
          if (r) onChange([...photos, r]);
        }}
      />
    </div>
  );
}

/** One document photo (ID or selfie) with camera capture. */
export function SinglePhoto({
  purpose,
  url,
  label,
  hint,
  onChange,
  selfie,
}: {
  purpose: Purpose;
  url?: string;
  label: string;
  hint: string;
  onChange: (r: { key: string; url: string }) => void;
  selfie?: boolean;
}) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const up = useUpload(purpose);
  return (
    <div className="mb-5">
      <span className="mb-1.5 block text-sm font-semibold">{label}</span>
      <button
        type="button"
        disabled={up.busy}
        onClick={() => input.current?.click()}
        className="relative flex h-44 w-full items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-tg-hint bg-tg-secondary"
      >
        {url ? (
          <img src={url} className="h-full w-full object-cover" alt="" />
        ) : up.busy ? (
          <Preloader />
        ) : (
          <span className="flex flex-col items-center gap-1 text-tg-hint">
            <Camera size={30} />
            <span className="text-sm">{t('reg.add_photo')}</span>
          </span>
        )}
        {url && (
          <span className="absolute bottom-2 right-2 rounded-full bg-green-600 p-1 text-white">
            <Check size={16} />
          </span>
        )}
      </button>
      <span className="mt-1 block text-sm text-tg-hint">{hint}</span>
      {up.error && <p className="mt-1 text-sm text-red-500">{up.error}</p>}
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture={selfie ? 'user' : 'environment'}
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          const r = await up.run(f);
          if (r) onChange(r);
        }}
      />
    </div>
  );
}

export function ProgressBar({ step, total }: { step: number; total: number }) {
  const t = useT();
  return (
    <div className="px-5 pt-3">
      <div className="mb-1 text-xs text-tg-hint">{t('reg.step_of', { n: step, total })}</div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-tg-secondary">
        <div className="h-full rounded-full bg-tg-button transition-all" style={{ width: `${(step / total) * 100}%` }} />
      </div>
    </div>
  );
}

/** Sticky bottom bar with the main action, like a native app. */
export function BottomActions({ children }: { children: ReactNode }) {
  return <div className="sticky bottom-0 z-10 space-y-2 bg-tg-bg px-5 pb-6 pt-3">{children}</div>;
}
